import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../styles/AddInquiry.css';

const AddInquiry = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_no: '',
    area: '',
    date: new Date().toISOString().split('T')[0],
    plan_to_join_days: '',
    reminder_date: '',
    source: 'Google',
    is_closed: false
  });

  // Handle Escape key to go back
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleBack();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Auto-calculate reminder date when date or plan_to_join_days changes
  useEffect(() => {
    calculateReminderDate();
  }, [formData.date, formData.plan_to_join_days]);

  const calculateReminderDate = () => {
    if (formData.date && formData.plan_to_join_days) {
      const days = parseInt(formData.plan_to_join_days);
      if (!isNaN(days) && days > 0) {
        const baseDate = new Date(formData.date);
        const reminderDate = new Date(baseDate);
        reminderDate.setDate(baseDate.getDate() + days);
        
        const formattedDate = reminderDate.toISOString().split('T')[0];
        setFormData(prev => ({
          ...prev,
          reminder_date: formattedDate
        }));
      }
    } else {
      // Clear reminder date if inputs are empty
      setFormData(prev => ({
        ...prev,
        reminder_date: ''
      }));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate mobile number
    if (!/^[0-9]{10}$/.test(formData.mobile_no)) {
      setError('Mobile number must be 10 digits');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/inquiry/add_inquiry.php', formData);
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/admin/inquiries');
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to add inquiry');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inquiry-page">
      <div className="inquiry-container">
        <div className="inquiry-header">
          <div className="inquiry-title-section">
            <button className="back-button" onClick={handleBack} title="Go back (ESC)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2>Add Inquiry</h2>
              <p>Fill in the details of the new inquiry</p>
            </div>
          </div>
          
          {/* Inquiry Closed Checkbox */}
          <label className="inquiry-closed-checkbox">
            <input
              type="checkbox"
              name="is_closed"
              checked={formData.is_closed}
              onChange={handleChange}
            />
            <span className="checkbox-label">Inquiry Closed</span>
            <span className="checkbox-info">✓ Check if student registered</span>
          </label>
        </div>

        <form onSubmit={handleSubmit} className="inquiry-form">
          {error && <div className="error-alert">{error}</div>}
          {success && <div className="success-alert">Inquiry added successfully!</div>}

          <div className="form-group">
            <label htmlFor="full_name">
              Full Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile_no">
              Mobile No. <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="mobile_no"
              name="mobile_no"
              value={formData.mobile_no}
              onChange={handleChange}
              placeholder="Enter mobile number"
              maxLength="10"
              pattern="[0-9]{10}"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="area">Area</label>
            <input
              type="text"
              id="area"
              name="area"
              value={formData.area}
              onChange={handleChange}
              placeholder="Enter area"
            />
          </div>

          <div className="form-group">
            <label htmlFor="date">
              Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="plan_to_join_days">
              Plan to Join in days
              <span className="field-info"> (Auto-calculates Reminder Date)</span>
            </label>
            <input
              type="number"
              id="plan_to_join_days"
              name="plan_to_join_days"
              value={formData.plan_to_join_days}
              onChange={handleChange}
              placeholder="Enter number of days"
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="reminder_date">
              Reminder Date
              <span className="auto-calculated"> (Auto-calculated)</span>
            </label>
            <input
              type="date"
              id="reminder_date"
              name="reminder_date"
              value={formData.reminder_date}
              readOnly
              className="readonly-field"
              placeholder="Will be calculated automatically"
            />
            {formData.reminder_date && (
              <p className="field-helper">
                📅 Reminder set for: {new Date(formData.reminder_date).toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="source">How you know about Ascent:</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Google"
                  checked={formData.source === 'Google'}
                  onChange={handleChange}
                />
                <span>Google</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Students Reference"
                  checked={formData.source === 'Students Reference'}
                  onChange={handleChange}
                />
                <span>Students Reference</span>
              </label>
              <label className="radio-label">
              <input
                 type="radio"
                 name="source"
                 value="SignBoard"
                  checked={formData.source === 'SignBoard'}
                  onChange={handleChange}
                  />
              <span>SignBoard</span>
            </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Social Media"
                  checked={formData.source === 'Social Media'}
                  onChange={handleChange}
                />
                <span>Social Media</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Other"
                  checked={formData.source === 'Other'}
                  onChange={handleChange}
                />
                <span>Other</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleBack}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading || success}
            >
              {loading ? 'Saving...' : success ? 'Saved!' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};



export default AddInquiry;
